import {
  CountryCode,
  CurrencyCode,
} from '_graphql/__generated__/globalTypes';
import {
  allVariationColumnKeys,
  type BulkProductRowVariation,
  type BulkProductVariationColumnMapping,
  parseBulkVariationNameOptionsForRow,
  rowHasNonEmptyMappedVariationContent,
} from './bulkVariationColumnParse';

export type { BulkProductRowVariation } from './bulkVariationColumnParse';

export type BulkProductUploadRow = {
  uuid: string;
  sku: string;
  title: string;
  description: string;
  price: number;
  costPrice: number;
  quantity: number;
  currency: CurrencyCode;
  country: CountryCode;
  published: boolean;
  avatar: string;
  media: string[];
  limited_stock?: boolean | null;
  inhouse?: boolean | null;
  weight?: number | null;
  tags?: string[];
  categoryId?: string;
  catalogIds?: string[];
  variationsDef?: BulkProductRowVariation[];
};

export type BulkChildRow = {
  uuid: string;
  parentSku: string;
  combinationString: string;
  price: number;
  costPrice: number;
  quantity: number;
  avatar: string;
  media: string[];
};

export type BulkProductUploadLine =
  | { type: 'parent'; excelRow: number; row: BulkProductUploadRow }
  | { type: 'child'; excelRow: number; row: BulkChildRow };

export type BulkProductUploadColumnMapping = BulkProductVariationColumnMapping & {
  title: string;
  description: string;
  sku: string;
  parentSku: string;
  combinationString: string;
  price: string;
  costPrice: string;
  quantity: string;
  imageUrl: string;
  avatarUrl: string;
  currency: string;
  published: string;
  weight: string;
  tags: string;
  limitedStock: string;
  inhouse: string;
  categoryName: string;
  catalogIds: string;
};

export type BulkProductUploadParseResult =
  | { ok: true; lines: BulkProductUploadLine[] }
  | { ok: false; errors: string[] };

export type BulkProductUploadParseOptions = {
  categoryNameToId?: Map<string, string>;
  tagNameToCanonical?: Map<string, string>;
  defaultCountry: CountryCode;
  defaultCurrency?: CurrencyCode;
  allowedCurrencyCodes?: Set<string>;
};

export function normalizeBulkUploadHeader(h: string): string {
  return h.trim().replace(/\s+/g, ' ').toLowerCase();
}

function normalizeHeader(h: string): string {
  return normalizeBulkUploadHeader(h);
}

function findColumnKey(
  headers: string[],
  normalizedTargets: string[],
): string | undefined {
  const byNorm = new Map<string, string>();
  for (const header of headers) {
    byNorm.set(normalizeHeader(header), header);
  }
  for (const t of normalizedTargets) {
    const found = byNorm.get(t);
    if (found) {
      return found;
    }
  }
  return undefined;
}

function findCategoryNameColumn(headers: string[]): string | undefined {
  return findColumnKey(headers, [
    'category name',
    'category_name',
    'category',
  ]);
}

function findImageUrlColumn(headers: string[]): string | undefined {
  const exact = new Set([
    'image url',
    'image_url',
    'image urls',
    'imageurls',
    'images',
    'product image',
    'product_image',
  ]);
  for (const header of headers) {
    const n = normalizeHeader(header);
    if (exact.has(n)) {
      return header;
    }
    if (n.includes('image') && n.includes('url')) {
      return header;
    }
  }
  return undefined;
}

function findTitleColumn(headers: string[]): string | undefined {
  return findColumnKey(headers, ['title', 'name', 'product name', 'product_name']);
}

function findDescriptionColumn(headers: string[]): string | undefined {
  return findColumnKey(headers, [
    'description',
    'desc',
    'product description',
    'product_description',
  ]);
}

function findAvatarColumn(headers: string[]): string | undefined {
  return findColumnKey(headers, ['avatar', 'avatar url', 'avatar_url', 'primary image']);
}

function findPublishedColumn(headers: string[]): string | undefined {
  return findColumnKey(headers, ['published', 'publish', 'is published', 'is_published']);
}

function findCurrencyColumn(headers: string[]): string | undefined {
  return findColumnKey(headers, ['currency']);
}

function findWeightColumn(headers: string[]): string | undefined {
  return findColumnKey(headers, ['weight', 'weight kg', 'weight_kg']);
}

function findTagsColumn(headers: string[]): string | undefined {
  return findColumnKey(headers, ['tags', 'tag']);
}

function findLimitedStockColumn(headers: string[]): string | undefined {
  return findColumnKey(headers, [
    'limited stock',
    'limited_stock',
    'limitedstock',
  ]);
}

function findInhouseColumn(headers: string[]): string | undefined {
  return findColumnKey(headers, ['inhouse', 'in house', 'in_house']);
}

function findCatalogIdsColumn(headers: string[]): string | undefined {
  return findColumnKey(headers, [
    'catalog id',
    'catalog_id',
    'catalog ids',
    'catalog_ids',
  ]);
}

function findParentSkuColumn(headers: string[]): string | undefined {
  return findColumnKey(headers, [
    'parent sku',
    'parent_sku',
    'parent-sku',
    'parentsku',
  ]);
}

function findCombinationStringColumn(headers: string[]): string | undefined {
  return findColumnKey(headers, [
    'combination string',
    'combination_string',
    'combination',
  ]);
}

function findVariationNameNColumn(
  headers: string[],
  n: number
): string | undefined {
  return findColumnKey(headers, [
    `variation name ${n}`,
    `variation_name_${n}`,
  ]);
}

function findVariationOptionsNColumn(
  headers: string[],
  n: number
): string | undefined {
  return findColumnKey(headers, [
    `variation options ${n}`,
    `variation_options_${n}`,
  ]);
}

export function isValidHttpUrl(s: string): boolean {
  const t = s.trim();
  if (!t) {
    return false;
  }
  try {
    const u = new URL(t);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

function parseMoney(raw: unknown): number | null {
  if (raw == null || raw === '') {
    return null;
  }
  const s = String(raw).trim().replace(/,/g, '').replace(/^\$\s?/, '');
  const n = Number(s);
  if (!Number.isFinite(n)) {
    return null;
  }
  return n;
}

function parseQuantity(raw: unknown): number | null {
  if (raw == null || raw === '') {
    return null;
  }
  const s = String(raw).trim().replace(/,/g, '');
  const n = Number(s);
  if (!Number.isFinite(n) || !Number.isInteger(n)) {
    return null;
  }
  return n;
}

function parseOptionalNumber(raw: unknown): number | null {
  if (raw == null || raw === '') {
    return null;
  }
  const s = String(raw).trim().replace(/,/g, '');
  const n = Number(s);
  if (!Number.isFinite(n)) {
    return null;
  }
  return n;
}

function parseBooleanLoose(raw: unknown): boolean | null {
  if (raw == null || raw === '') {
    return null;
  }
  const s = String(raw).trim().toLowerCase();
  if (['true', 'yes', 'y', '1', 'published'].includes(s)) {
    return true;
  }
  if (['false', 'no', 'n', '0', 'unpublished'].includes(s)) {
    return false;
  }
  return null;
}

function toCurrencyEnum(raw: string): CurrencyCode | null {
  const u = raw.trim().toUpperCase();
  if ((Object.values(CurrencyCode) as string[]).includes(u)) {
    return u as CurrencyCode;
  }
  return null;
}

function parseCurrencyCell(
  raw: string,
  allowed?: Set<string>,
): { ok: true; value: CurrencyCode } | { ok: false; message: string } {
  const u = raw.trim().toUpperCase();
  if (allowed && allowed.size > 0 && !allowed.has(u)) {
    return {
      ok: false,
      message: `CURRENCY must be one of: ${Array.from(allowed).sort().join(', ')}`,
    };
  }
  const c = toCurrencyEnum(u);
  if (!c) {
    const fallbackList = allowed && allowed.size > 0
      ? Array.from(allowed).sort().join(', ')
      : (Object.values(CurrencyCode) as string[]).join(', ');
    return {
      ok: false,
      message: `CURRENCY must be a valid code (${fallbackList})`,
    };
  }
  return { ok: true, value: c };
}

function splitImageUrls(cell: string): string[] {
  const cleaned = cell.trim();
  if (!cleaned) {
    return [];
  }

  const normalizePart = (part: string) =>
    part.trim().replace(/^,+|,+$/g, '');

  if (!cleaned.includes(',')) {
    return [normalizePart(cleaned)].filter(Boolean);
  }

  const parts = cleaned
    .split(/,(?=\s*(?:https?:\/\/|www\.))/i)
    .map(normalizePart)
    .filter(Boolean);

  return parts.length > 0 ? parts : [normalizePart(cleaned)].filter(Boolean);
}

function splitCatalogIds(cell: string): string[] {
  return cell
    .split(/[,;]/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function currencyForCountry(country: CountryCode): CurrencyCode {
  if (country === CountryCode.GH) {
    return CurrencyCode.GHS;
  }
  if (country === CountryCode.NG) {
    return CurrencyCode.NGN;
  }
  if (country === CountryCode.ZA) {
    return CurrencyCode.ZAR;
  }
  return CurrencyCode.GHS;
}

function resolveDefaultCurrency(
  options: BulkProductUploadParseOptions | undefined,
  defaultCountry: CountryCode,
): CurrencyCode {
  if (options?.defaultCurrency != null) {
    return options.defaultCurrency;
  }
  return currencyForCountry(defaultCountry);
}

export function suggestBulkUploadColumnMapping(
  headers: string[],
): BulkProductUploadColumnMapping {
  const variation: Partial<BulkProductVariationColumnMapping> = {};
  for (let n = 1; n <= 7; n++) {
    (variation as Record<string, string>)[`variationName${n}`] =
      findVariationNameNColumn(headers, n) ?? '';
    (variation as Record<string, string>)[`variationOptions${n}`] =
      findVariationOptionsNColumn(headers, n) ?? '';
  }

  return {
    title: findTitleColumn(headers) ?? '',
    description: findDescriptionColumn(headers) ?? '',
    sku: findColumnKey(headers, ['sku']) ?? '',
    parentSku: findParentSkuColumn(headers) ?? '',
    combinationString: findCombinationStringColumn(headers) ?? '',
    price: findColumnKey(headers, ['price']) ?? '',
    costPrice: findColumnKey(headers, ['cost price', 'costprice']) ?? '',
    quantity: findColumnKey(headers, ['quantity', 'qty']) ?? '',
    imageUrl: findImageUrlColumn(headers) ?? '',
    avatarUrl: findAvatarColumn(headers) ?? '',
    currency: findCurrencyColumn(headers) ?? '',
    published: findPublishedColumn(headers) ?? '',
    weight: findWeightColumn(headers) ?? '',
    tags: findTagsColumn(headers) ?? '',
    limitedStock: findLimitedStockColumn(headers) ?? '',
    inhouse: findInhouseColumn(headers) ?? '',
    categoryName: findCategoryNameColumn(headers) ?? '',
    catalogIds: findCatalogIdsColumn(headers) ?? '',
    ...variation,
  } as BulkProductUploadColumnMapping;
}

export function parseBulkProductUploadWithMapping(
  rows: Record<string, unknown>[],
  mapping: BulkProductUploadColumnMapping,
  options?: BulkProductUploadParseOptions,
): BulkProductUploadParseResult {
  if (!rows.length) {
    return {
      ok: false,
      errors: ['The file must contain at least one row of data.'],
    };
  }

  const missingSelect: string[] = [];
  if (!mapping.title) {
    missingSelect.push('TITLE');
  }
  if (!mapping.description) {
    missingSelect.push('DESCRIPTION');
  }
  if (!mapping.sku) {
    missingSelect.push('SKU');
  }
  if (!mapping.price) {
    missingSelect.push('PRICE');
  }
  if (!mapping.costPrice) {
    missingSelect.push('COST PRICE');
  }
  if (!mapping.quantity) {
    missingSelect.push('QUANTITY');
  }
  if (missingSelect.length) {
    return {
      ok: false,
      errors: [`Choose a file column for: ${missingSelect.join(', ')}.`],
    };
  }

  const defaultCountry = options?.defaultCountry;
  if (!defaultCountry) {
    return {
      ok: false,
      errors: ['Country context is required to validate bulk products.'],
    };
  }

  const requiredCols = [
    mapping.title,
    mapping.description,
    mapping.sku,
    mapping.price,
    mapping.costPrice,
    mapping.quantity,
  ];
  const optionalCols = [
    mapping.imageUrl,
    mapping.parentSku,
    mapping.combinationString,
    ...allVariationColumnKeys(mapping),
    mapping.avatarUrl,
    mapping.currency,
    mapping.published,
    mapping.weight,
    mapping.tags,
    mapping.limitedStock,
    mapping.inhouse,
    mapping.categoryName,
    mapping.catalogIds,
  ].filter((c) => c && String(c).trim());
  const allMapped = [...requiredCols, ...optionalCols];
  if (new Set(allMapped).size !== allMapped.length) {
    return {
      ok: false,
      errors: ['Each product field must map to a different file column.'],
    };
  }

  function isSheetRowCompletelyEmpty(rawRow: Record<string, unknown>): boolean {
    for (const v of Object.values(rawRow)) {
      if (v != null && String(v).trim() !== '') {
        return false;
      }
    }
    return true;
  }

  const rowsWithData = rows
    .map((rawRow, idx) => ({
      rawRow,
      excelRow: idx + 2,
    }))
    .filter(({ rawRow }) => !isSheetRowCompletelyEmpty(rawRow));

  if (!rowsWithData.length) {
    return {
      ok: false,
      errors: ['The file must contain at least one row of data.'],
    };
  }

  const titleCol = mapping.title;
  const descCol = mapping.description;
  const skuCol = mapping.sku;
  const priceCol = mapping.price;
  const costCol = mapping.costPrice;
  const qtyCol = mapping.quantity;
  const imageKey = mapping.imageUrl.trim() ? mapping.imageUrl : undefined;
  const avatarKey = mapping.avatarUrl.trim() ? mapping.avatarUrl : undefined;
  const currencyKey = mapping.currency.trim() ? mapping.currency : undefined;
  const publishedKey = mapping.published.trim() ? mapping.published : undefined;
  const weightKey = mapping.weight.trim() ? mapping.weight : undefined;
  const tagsKey = mapping.tags.trim() ? mapping.tags : undefined;
  const limitedKey = mapping.limitedStock.trim()
    ? mapping.limitedStock
    : undefined;
  const inhouseKey = mapping.inhouse.trim() ? mapping.inhouse : undefined;
  const categoryNameCol = mapping.categoryName.trim()
    ? mapping.categoryName
    : undefined;
  const catalogIdsCol = mapping.catalogIds.trim()
    ? mapping.catalogIds
    : undefined;
  const parentSkuCol = mapping.parentSku.trim() ? mapping.parentSku : undefined;
  const combinationStrCol = mapping.combinationString.trim()
    ? mapping.combinationString
    : undefined;

  const lines: BulkProductUploadLine[] = [];
  const errors: string[] = [];

  rowsWithData.forEach(({ rawRow, excelRow: rowNum }) => {
    const rawSku = String(rawRow[skuCol] ?? '').trim();
    const rawParentSku = parentSkuCol
      ? String(rawRow[parentSkuCol] ?? '').trim()
      : '';
    if (rawSku && rawParentSku) {
      errors.push(
        `Row ${rowNum}: use SKU (parent) or PARENT_SKU (combination), not both`,
      );
      return;
    }
    if (!rawSku && !rawParentSku) {
      errors.push(
        `Row ${rowNum}: set SKU (parent) or PARENT_SKU (combination line)`,
      );
      return;
    }

    if (!rawSku && rawParentSku) {
      const childErrs: string[] = [];
      if (!parentSkuCol) {
        childErrs.push('Map a PARENT_SKU column to import combination lines');
      }
      if (!combinationStrCol) {
        childErrs.push('Map a COMBINATION_STRING column to import combination lines');
      }
      if (rowHasNonEmptyMappedVariationContent(rawRow, mapping)) {
        childErrs.push(
          'VARIATION_NAME_n / VARIATION_OPTIONS_n apply only to parent rows; clear them for combination lines',
        );
      }
      const combStr = combinationStrCol
        ? String(rawRow[combinationStrCol] ?? '').trim()
        : '';
      if (!combStr) {
        childErrs.push('COMBINATION_STRING is required for combination lines');
      }
      const cPrice = parseMoney(rawRow[priceCol]);
      const cCost = parseMoney(rawRow[costCol]);
      const cQty = parseQuantity(rawRow[qtyCol]);
      if (cPrice === null) {
        childErrs.push('PRICE must be a valid number');
      } else if (cPrice < 0) {
        childErrs.push('PRICE must be zero or greater');
      }
      if (cCost === null) {
        childErrs.push('COST PRICE must be a valid number');
      } else if (cCost < 0) {
        childErrs.push('COST PRICE must be zero or greater');
      }
      if (cQty === null) {
        childErrs.push('QUANTITY must be a whole number');
      } else if (cQty < 0) {
        childErrs.push('QUANTITY must be zero or greater');
      }
      const cMedia: string[] = [];
      if (imageKey) {
        const cell = rawRow[imageKey];
        if (cell != null && String(cell).trim() !== '') {
          for (const p of splitImageUrls(String(cell))) {
            if (!isValidHttpUrl(p)) {
              childErrs.push(`Invalid image URL: ${p}`);
            } else {
              cMedia.push(p.trim());
            }
          }
        }
      }
      let cAvatar = '';
      if (avatarKey) {
        const cell = rawRow[avatarKey];
        if (cell != null && String(cell).trim() !== '') {
          const a = String(cell).trim();
          if (!isValidHttpUrl(a)) {
            childErrs.push(`Invalid AVATAR URL: ${a}`);
          } else {
            cAvatar = a;
          }
        }
      }
      if (!cAvatar && cMedia.length > 0) {
        cAvatar = cMedia[0]!;
      }
      if (!cAvatar) {
        childErrs.push(
          'AVATAR: map IMAGE URL or AVATAR URL, or at least one valid image URL, for this combination',
        );
      }
      if (childErrs.length) {
        errors.push(`Row ${rowNum}: ${childErrs.join('; ')}`);
        return;
      }
      const mediaDed = cAvatar && !cMedia.includes(cAvatar) ? [cAvatar, ...cMedia] : [...cMedia];
      lines.push({
        type: 'child',
        excelRow: rowNum,
        row: {
          uuid: crypto.randomUUID(),
          parentSku: rawParentSku,
          combinationString: combStr,
          price: cPrice!,
          costPrice: cCost!,
          quantity: cQty!,
          avatar: cAvatar,
          media: mediaDed.length ? mediaDed : cAvatar ? [cAvatar] : [],
        },
      });
      return;
    }

    const title = String(rawRow[titleCol] ?? '').trim();
    const description = String(rawRow[descCol] ?? '').trim();
    const sku = rawSku;
    const price = parseMoney(rawRow[priceCol]);
    const costPrice = parseMoney(rawRow[costCol]);
    const quantity = parseQuantity(rawRow[qtyCol]);

    const rowErrs: string[] = [];
    if (!title) {
      rowErrs.push('TITLE is required on parent rows');
    }
    if (!description) {
      rowErrs.push('DESCRIPTION is required on parent rows');
    }
    if (price === null) {
      rowErrs.push('PRICE must be a valid number');
    } else if (price < 0) {
      rowErrs.push('PRICE must be zero or greater');
    }
    if (costPrice === null) {
      rowErrs.push('COST PRICE must be a valid number');
    } else if (costPrice < 0) {
      rowErrs.push('COST PRICE must be zero or greater');
    }
    if (quantity === null) {
      rowErrs.push('QUANTITY must be a whole number');
    } else if (quantity < 0) {
      rowErrs.push('QUANTITY must be zero or greater');
    }

    const allowedCur = options?.allowedCurrencyCodes;
    let currency: CurrencyCode | null = null;
    if (currencyKey) {
      const rawCur = String(rawRow[currencyKey] ?? '').trim();
      if (rawCur) {
        const parsed = parseCurrencyCell(rawCur, allowedCur);
        if (!parsed.ok) {
          rowErrs.push(parsed.message);
        } else {
          currency = parsed.value;
        }
      } else {
        currency = resolveDefaultCurrency(options, defaultCountry);
      }
    } else {
      currency = resolveDefaultCurrency(options, defaultCountry);
    }

    let published = true;
    if (publishedKey) {
      const rawPub = rawRow[publishedKey];
      const b = parseBooleanLoose(rawPub);
      if (b === null && rawPub != null && String(rawPub).trim() !== '') {
        rowErrs.push('PUBLISHED must be TRUE or FALSE (or yes/no, 1/0)');
      } else if (b !== null) {
        published = b;
      }
    }

    const media: string[] = [];
    if (imageKey) {
      const cell = rawRow[imageKey];
      if (cell != null && String(cell).trim() !== '') {
        const parts = splitImageUrls(String(cell));
        for (const p of parts) {
          if (!isValidHttpUrl(p)) {
            rowErrs.push(`Invalid image URL: ${p}`);
          } else {
            media.push(p.trim());
          }
        }
      }
    }

    let avatar = '';
    if (avatarKey) {
      const cell = rawRow[avatarKey];
      if (cell != null && String(cell).trim() !== '') {
        const a = String(cell).trim();
        if (!isValidHttpUrl(a)) {
          rowErrs.push(`Invalid AVATAR URL: ${a}`);
        } else {
          avatar = a;
        }
      }
    }
    if (!avatar && media.length > 0) {
      avatar = media[0];
    }
    if (!avatar) {
      rowErrs.push(
        'AVATAR is required: map IMAGE URL or AVATAR URL, or provide at least one valid image URL',
      );
    }

    let weight: number | null | undefined;
    if (weightKey) {
      const w = parseOptionalNumber(rawRow[weightKey]);
      if (rawRow[weightKey] != null && String(rawRow[weightKey]).trim() !== '') {
        if (w === null) {
          rowErrs.push('WEIGHT must be a valid number');
        } else if (w < 0) {
          rowErrs.push('WEIGHT must be zero or greater');
        } else {
          weight = w;
        }
      }
    }

    let tags: string[] | undefined;
    if (tagsKey) {
      const rawT = String(rawRow[tagsKey] ?? '').trim();
      if (rawT) {
        const parts = rawT.split(/[,;]/).map((t) => t.trim()).filter(Boolean);
        const tagLookup = options?.tagNameToCanonical;
        if (tagLookup && tagLookup.size > 0) {
          const resolved: string[] = [];
          for (const part of parts) {
            const canon = tagLookup.get(normalizeHeader(part));
            if (!canon) {
              rowErrs.push(`Unknown tag: ${part}`);
            } else {
              resolved.push(canon);
            }
          }
          tags = Array.from(new Set(resolved));
        } else {
          tags = Array.from(new Set(parts));
        }
        if (!tags.length) {
          tags = undefined;
        }
      }
    }

    let limited_stock: boolean | null | undefined;
    if (limitedKey) {
      const rawL = rawRow[limitedKey];
      if (rawL != null && String(rawL).trim() !== '') {
        const b = parseBooleanLoose(rawL);
        if (b === null) {
          rowErrs.push('LIMITED STOCK must be TRUE or FALSE (or yes/no, 1/0)');
        } else {
          limited_stock = b;
        }
      }
    }

    let inhouse: boolean | null | undefined;
    if (inhouseKey) {
      const rawI = rawRow[inhouseKey];
      if (rawI != null && String(rawI).trim() !== '') {
        const b = parseBooleanLoose(rawI);
        if (b === null) {
          rowErrs.push('INHOUSE must be TRUE or FALSE (or yes/no, 1/0)');
        } else {
          inhouse = b;
        }
      }
    }

    let catalogIds: string[] | undefined;
    if (catalogIdsCol) {
      const rawC = String(rawRow[catalogIdsCol] ?? '').trim();
      if (rawC) {
        catalogIds = splitCatalogIds(rawC);
      }
    }

    let categoryId: string | undefined;
    if (categoryNameCol) {
      const rawName = String(rawRow[categoryNameCol] ?? '').trim();
      if (rawName) {
        const map = options?.categoryNameToId;
        if (map && map.size > 0) {
          const id = map.get(normalizeHeader(rawName));
          if (id) {
            categoryId = id;
          } else {
            rowErrs.push(`Unknown category name: ${rawName}`);
          }
        } else {
          rowErrs.push(
            'Category name is set but category lookup is missing; keep the bulk upload modal open until categories finish loading, or try again.',
          );
        }
      }
    }

    const parsedVariations = parseBulkVariationNameOptionsForRow(rawRow, mapping, sku);
    if (!parsedVariations.ok) {
      rowErrs.push(parsedVariations.error);
    }

    if (rowErrs.length) {
      errors.push(`Row ${rowNum}: ${rowErrs.join('; ')}`);
      return;
    }

    const mediaDeduped =
      avatar && !media.includes(avatar) ? [avatar, ...media] : [...media];

    const rowOut: BulkProductUploadRow = {
      uuid: crypto.randomUUID(),
      sku,
      title,
      description,
      price: price!,
      costPrice: costPrice!,
      quantity: quantity!,
      currency: currency!,
      country: defaultCountry,
      published,
      avatar,
      media: mediaDeduped.length ? mediaDeduped : avatar ? [avatar] : [],
    };
    if (categoryId) {
      rowOut.categoryId = categoryId;
    }
    if (catalogIds?.length) {
      rowOut.catalogIds = catalogIds;
    }
    if (limited_stock !== undefined) {
      rowOut.limited_stock = limited_stock;
    }
    if (inhouse !== undefined) {
      rowOut.inhouse = inhouse;
    }
    if (weight !== undefined && weight !== null) {
      rowOut.weight = weight;
    }
    if (tags != null && tags.length > 0) {
      rowOut.tags = tags;
    }
    if (parsedVariations.ok && parsedVariations.variations.length > 0) {
      rowOut.variationsDef = parsedVariations.variations;
    }

    lines.push({ type: 'parent', excelRow: rowNum, row: rowOut });
  });

  if (errors.length) {
    return { ok: false, errors };
  }
  if (!lines.length) {
    return {
      ok: false,
      errors: ['The file must contain at least one row of data.'],
    };
  }
  if (!lines.some((l) => l.type === 'parent')) {
    return {
      ok: false,
      errors: [
        'Add at least one parent product row: set SKU in the file (combination lines use PARENT_SKU only, not SKU).',
      ],
    };
  }
  return { ok: true, lines };
}

export function parseBulkProductUploadSheet(
  rows: Record<string, unknown>[],
  options?: BulkProductUploadParseOptions,
): BulkProductUploadParseResult {
  if (!rows.length) {
    return {
      ok: false,
      errors: ['The file must contain at least one row of data.'],
    };
  }
  const headers = Object.keys(rows[0]);
  return parseBulkProductUploadWithMapping(
    rows,
    suggestBulkUploadColumnMapping(headers),
    options,
  );
}
